import { useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { formatQtyWithPackage } from "@/lib/units";
import CostVarianceBadge from "@/components/CostVarianceBadge";
import { computeVariance } from "@/lib/costVariance";

export default function HistoryPage() {
  const [runs, setRuns] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [filterProduct, setFilterProduct] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      const [r, p, m] = await Promise.all([
        api.get("/production"),
        api.get("/products", { params: { include_deleted: true } }),
        api.get("/materials"),
      ]);
      setRuns(r.data);
      setProducts(p.data);
      setMaterials(m.data);
    })();
  }, []);

  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matUnit = (id) => materials.find((m) => m.id === id)?.unit || "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return runs.filter((r) => {
      if (filterProduct && r.product_id !== filterProduct) return false;
      if (from && r.created_at < from) return false;
      if (to && r.created_at > to + "T23:59:59") return false;
      if (q) {
        const fields = [r.batch_code, prodName(r.product_id)].filter(Boolean).map((x) => x.toLowerCase());
        if (!fields.some((f) => f.includes(q))) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, filterProduct, from, to, search, products]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, r) => {
        acc.qty += r.quantity;
        acc.cost += r.total_cost;
        const product = products.find((p) => p.id === r.product_id);
        const v = computeVariance(r.unit_cost, product);
        if (v) {
          acc.compared += 1;
          if (v.overTolerance) acc.outOfTolerance += 1;
        }
        return acc;
      },
      { qty: 0, cost: 0, compared: 0, outOfTolerance: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, products]);

  const exportCsv = () => {
    const rows = [
      [
        "Šarže",
        "Datum",
        "Výrobek",
        "Vyrobeno",
        "Výrobní cena/ks (Kč)",
        "Plán. cena/ks (Kč)",
        "Rozdíl (%)",
        "Mimo toleranci",
        "Celkové náklady (Kč)",
      ],
      ...filtered.map((r) => {
        const product = products.find((p) => p.id === r.product_id);
        const v = computeVariance(r.unit_cost, product);
        return [
          r.batch_code,
          new Date(r.created_at).toLocaleString("cs-CZ"),
          prodName(r.product_id),
          r.quantity,
          r.unit_cost.toFixed(4),
          v ? v.planned.toFixed(2) : "",
          v ? v.pct.toFixed(1) : "",
          v ? (v.overTolerance ? "ANO" : "NE") : "",
          r.total_cost.toFixed(2),
        ];
      }),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cafecko-historie-vyroby-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="history-page">
      <PageHeader
        eyebrow="Historie"
        title="Historie výroby a šarží"
        description="Plný přehled všech výrob, jejich šarží a výrobních nákladů. Rozkliknutím zobrazíte spotřebované suroviny s nákupními cenami."
        actions={
          <button
            data-testid="history-export-csv"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#E5E1D8] bg-white hover:bg-[#F9F6F0]"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />

      <div className="card-cafe p-4 mb-6 space-y-3">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Hledat podle kódu šarže nebo výrobku…"
          testid="history-search"
        />
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <div className="text-xs text-[#57534E] mb-1">Výrobek</div>
            <select
              data-testid="history-filter-product"
              className="input-cafe py-1.5"
              value={filterProduct}
              onChange={(e) => setFilterProduct(e.target.value)}
            >
              <option value="">— všechny —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs text-[#57534E] mb-1">Od</div>
            <input
              type="date"
              data-testid="history-filter-from"
              className="input-cafe py-1.5"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <div className="text-xs text-[#57534E] mb-1">Do</div>
            <input
              type="date"
              data-testid="history-filter-to"
              className="input-cafe py-1.5"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="ml-auto flex items-center gap-6 text-sm text-[#57534E]">
            <div>
              <span className="label-eyebrow mr-2">Vyrobeno</span>
              <span className="mono">{formatQty(totals.qty)} ks</span>
            </div>
            <div>
              <span className="label-eyebrow mr-2">Hodnota</span>
              <span className="mono">{formatCZK(totals.cost)}</span>
            </div>
            {totals.compared > 0 && (
              <div
                data-testid="history-out-of-tolerance"
                className={totals.outOfTolerance > 0 ? "text-[#9E4747]" : "text-[#376A4D]"}
              >
                <span className="label-eyebrow mr-2">Mimo toleranci</span>
                <span className="mono font-semibold">
                  {totals.outOfTolerance} / {totals.compared}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-cafe overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6F0]">
            <tr className="text-left">
              <th className="p-3 w-8"></th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Šarže</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Datum</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Výrobek</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Ks</th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">
                Výrobní cena/ks
              </th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">
                Plán. cena/ks
              </th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">
                Rozdíl
              </th>
              <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">
                Celkem
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="9" className="p-10 text-center text-[#57534E]">
                  Žádné záznamy.
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const product = products.find((p) => p.id === r.product_id);
                const planned = Number(product?.planned_unit_cost || 0);
                return (
                <Fragment key={r.id}>
                  <tr
                    className="border-t border-[#E5E1D8] hover:bg-[#F9F6F0]/50 cursor-pointer"
                    onClick={() => setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}
                    data-testid={`history-row-${r.id}`}
                  >
                    <td className="p-3 text-[#57534E]">
                      {expanded[r.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="p-3 mono text-xs">{r.batch_code}</td>
                    <td className="p-3">{formatDateTime(r.created_at)}</td>
                    <td className="p-3 font-medium">{prodName(r.product_id)}</td>
                    <td className="p-3 text-right">{formatQtyWithPackage(r.quantity, product, "ks")}</td>
                    <td className="p-3 text-right">{formatCZK(r.unit_cost)}</td>
                    <td className="p-3 text-right">
                      {planned > 0 ? formatCZK(planned) : <span className="text-[#A8A29E]">—</span>}
                    </td>
                    <td className="p-3 text-right">
                      <CostVarianceBadge actualUnitCost={r.unit_cost} product={product} />
                    </td>
                    <td className="p-3 text-right font-medium">{formatCZK(r.total_cost)}</td>
                  </tr>
                  {expanded[r.id] && (
                    <tr className="bg-[#F9F6F0]/60">
                      <td></td>
                      <td colSpan="8" className="p-4">
                        <div className="label-eyebrow mb-2">Spotřebované suroviny (FIFO)</div>
                        <div className="overflow-hidden rounded-md border border-[#E5E1D8] bg-white">
                          <table className="w-full text-sm">
                            <thead className="bg-[#F9F6F0]">
                              <tr className="text-left">
                                <th className="p-2 text-xs text-[#57534E] font-medium">Šarže</th>
                                <th className="p-2 text-xs text-[#57534E] font-medium">Surovina</th>
                                <th className="p-2 text-xs text-[#57534E] font-medium text-right">Množství</th>
                                <th className="p-2 text-xs text-[#57534E] font-medium text-right">Nák. cena</th>
                                <th className="p-2 text-xs text-[#57534E] font-medium text-right">Náklad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.consumed.map((c, idx) => (
                                <tr key={idx} className="border-t border-[#E5E1D8]">
                                  <td className="p-2 mono text-xs">{c.batch_code}</td>
                                  <td className="p-2">{matName(c.material_id)}</td>
                                  <td className="p-2 text-right">
                                    {formatQty(c.quantity, matUnit(c.material_id))}
                                  </td>
                                  <td className="p-2 text-right">{formatCZK(c.unit_price)}</td>
                                  <td className="p-2 text-right">{formatCZK(c.cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
